/**
 * Dice material physics — the numeric half of docs/03-AUDIO-BIBLE.md §2.
 *
 * Changing a value here changes how the game FEELS more than almost any other
 * file, so the Audio Bible table and this file must be edited together.
 */

export type DiceMaterial = 'wood' | 'stone' | 'metal' | 'magic';

export interface MaterialProfile {
  /** Fundamental of the body resonance, Hz. */
  readonly baseFreq: number;
  /** Modal ratios. Inharmonic ratios read as "struck object", not "musical note". */
  readonly modes: readonly number[];
  /** Resonator Q. High Q = long metallic ring; low Q = dry knock. */
  readonly q: number;
  /** Exponential decay time constant, seconds. */
  readonly decay: number;
  /** Share of the click that is broadband noise vs. tuned resonance, 0..1. */
  readonly noise: number;
  /** Bounce count range for one throw. */
  readonly bounces: readonly [number, number];
  /** Attack sharpness, seconds. Shorter = harder surface. */
  readonly attack: number;
  /** Extra detuned shimmer partial (magic dice only). */
  readonly shimmer?: number;
}

export const MATERIALS: Readonly<Record<DiceMaterial, MaterialProfile>> = {
  wood: {
    baseFreq: 210,
    modes: [1.0, 2.4, 3.9],
    q: 9,
    decay: 0.16,
    noise: 0.55,
    bounces: [3, 5],
    attack: 0.0016,
  },
  stone: {
    baseFreq: 330,
    modes: [1.0, 2.7, 5.1],
    q: 14,
    decay: 0.11,
    noise: 0.4,
    bounces: [2, 4],
    attack: 0.0011,
  },
  metal: {
    baseFreq: 620,
    modes: [1.0, 2.76, 5.4, 8.9],
    q: 46,
    decay: 0.85,
    noise: 0.18,
    bounces: [4, 7],
    attack: 0.0008,
  },
  magic: {
    baseFreq: 480,
    modes: [1.0, 2.0, 3.0, 4.5],
    q: 28,
    decay: 0.55,
    noise: 0.25,
    bounces: [3, 5],
    attack: 0.0014,
    shimmer: 1.5,
  },
};

/**
 * Every die lands on the same table, so a fixed share of wood resonance is mixed
 * into every impact. Without it, a metal die and a stone die sound like they are
 * in two different rooms.
 */
export const TABLE_RESONANCE_MIX = 0.08;
export const TABLE_PROFILE = MATERIALS.wood;

/** Audio Bible §1.5 / Style Bible §1.5 — rarity signatures share one scale. */
export type Rarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary' | 'mythic';

export interface RaritySignature {
  readonly duration: number;
  /** Semitone offsets from the motif root. */
  readonly intervals: readonly number[];
  readonly timbre: 'marimba' | 'glockenspiel' | 'bell';
  readonly shimmer: boolean;
  readonly subDrop: boolean;
  readonly reverse: boolean;
}

/**
 * All intervals are drawn from the D-minor motif's own scale so that a reward
 * sound and the soundtrack occupy the same tonal world (Audio Bible §4).
 */
export const RARITY_SIGNATURES: Readonly<Record<Rarity, RaritySignature>> = {
  common: { duration: 0.25, intervals: [0], timbre: 'marimba', shimmer: false, subDrop: false, reverse: false },
  uncommon: { duration: 0.35, intervals: [0, 7], timbre: 'marimba', shimmer: false, subDrop: false, reverse: false },
  rare: { duration: 0.5, intervals: [0, 3, 7], timbre: 'glockenspiel', shimmer: false, subDrop: false, reverse: false },
  epic: { duration: 0.7, intervals: [0, 3, 7, 10], timbre: 'glockenspiel', shimmer: true, subDrop: false, reverse: false },
  legendary: { duration: 1.0, intervals: [0, 3, 7, 12, 15], timbre: 'bell', shimmer: true, subDrop: false, reverse: false },
  mythic: { duration: 1.4, intervals: [0, 3, 7, 12, 15, 19], timbre: 'bell', shimmer: true, subDrop: true, reverse: true },
};

/** THE LAST ROLL main motif: D - A - F - E (Audio Bible §6). */
export const MOTIF_ROOT_HZ = 146.83; // D3
export const MOTIF_INTERVALS: readonly number[] = [0, 7, 3, 2];

export function semitonesToHz(rootHz: number, semitones: number): number {
  return rootHz * Math.pow(2, semitones / 12);
}

/**
 * Mix bus layout (Audio Bible §7). `tactile` deliberately sits 6 dB above music
 * and receives no reverb: dice and cards must feel like they are in front of the
 * listener, not in the room.
 */
export type BusName = 'tactile' | 'world' | 'music' | 'voice' | 'ui';

export interface BusConfig {
  readonly gain: number;
  readonly reverbSend: number;
}

export const BUSES: Readonly<Record<BusName, BusConfig>> = {
  tactile: { gain: 1.0, reverbSend: 0.0 },
  world: { gain: 0.5, reverbSend: 0.35 },
  music: { gain: 0.5, reverbSend: 0.2 },
  voice: { gain: 0.75, reverbSend: 0.15 },
  ui: { gain: 0.6, reverbSend: 0.0 },
};

/** Music ducks when dice hit the table, so the roll always reads. */
export const DUCK_DB = -4;
export const DUCK_RELEASE_S = 0.4;
export const REVERB_RT60_S = 0.6;
